import {financialFields, calculatePaidTotal, calculateBill as canonicalBill} from "../../services/billingService";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

import PaymentReceiptModal from "./PaymentReceiptModal";

import {
  calculateBill,
  createReceiptNumber,
  formatMoney,
  roundMoney,
  toAmount,
} from "../../services/billingService";

import {
  getBusinessDateKey,
} from "../../utils/businessDate";

import {
  logPaymentReceived,
} from "../../services/auditLogService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const getDateObject = (value) => {
  if (!value) return null;

  if (
    typeof value?.toDate ===
    "function"
  ) {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(
      value.seconds * 1000
    );
  }

  const date = new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() ||
  0;

const formatDateTime = (value) => {
  const date =
    getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const getPrimaryGuest = (
  stay = {}
) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) || stay.guests[0]
    );
  }

  return {
    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",

    address:
      stay.address || "",

    idType:
      stay.idType || "",

    idNumber:
      stay.idNumber || "",
  };
};

const getBookingReference = (
  stay = {}
) =>
  clean(
    stay.otaBookingId ||
      stay.bookingReference ||
      stay.corporateReference ||
      stay.bookingId
  );

const calculateStayBill = (stay = {}) => canonicalBill(stay);
const getPendingAmount = (stay = {}) => canonicalBill(stay).balance;
const getPaymentStatus = (stay = {}) => canonicalBill(stay).paymentStatus;

const createPaymentForm = (
  pendingAmount = 0
) => ({
  amount:
    pendingAmount > 0
      ? String(pendingAmount)
      : "",

  paymentMode: "Cash",

  transactionReference: "",

  note:
    "Pending amount received",

  receivedBy: "",

  printReceipt: true,
});

/* -------------------------------------------------------------------------- */
/*                              Reusable Field                                */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  required = false,
  error = "",
  children,
}) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}

        {required && (
          <span style={requiredStyle}>
            {" "}
            *
          </span>
        )}
      </span>

      {children}

      {error && (
        <span style={fieldErrorStyle}>
          {error}
        </span>
      )}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Pending Payments Panel                               */
/* -------------------------------------------------------------------------- */

function PendingPaymentsPanel({
  hotelId,
  hotelName,
}) {
  const [stays, setStays] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("pending");

  const [sortBy, setSortBy] =
    useState("highest");

  const [
    selectedStay,
    setSelectedStay,
  ] = useState(null);

  const [paymentForm, setPaymentForm] =
    useState(createPaymentForm());

  const [errors, setErrors] =
    useState({});

  const [saving, setSaving] =
    useState(false);

  const [
    receiptData,
    setReceiptData,
  ] = useState(null);

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setStays([]);
      setLoading(false);

      setLoadError(
        "Hotel ID missing hai."
      );

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const staysQuery = query(
      collection(
        db,
        "hotelStays"
      ),

      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      staysQuery,

      (snapshot) => {
        const list =
          snapshot.docs
            .map((stayDocument) => ({
              id:
                stayDocument.id,

              stayId:
                stayDocument.id,

              ...stayDocument.data(),
            }))
            .filter(
              (stay) =>
                getPendingAmount(
                  stay
                ) > 0
            )
            .sort(
              (
                firstStay,
                secondStay
              ) =>
                getTimestamp(
                  secondStay.actualCheckOutAt ||
                    secondStay.checkedOutAt ||
                    secondStay.checkInAt
                ) -
                getTimestamp(
                  firstStay.actualCheckOutAt ||
                    firstStay.checkedOutAt ||
                    firstStay.checkInAt
                )
            );

        setStays(list);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Pending payments load error:",
          error
        );

        setStays([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Pending payments load nahi hui."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                                Summary                                    */
  /* ------------------------------------------------------------------------ */

  const summary = useMemo(() => {
    const result = {
      totalGuests: stays.length,

      totalPending: 0,

      checkedInPending: 0,

      checkedOutPending: 0,

      partialPayments: 0,

      noPayment: 0,

      oldestPendingAt: null,
    };

    stays.forEach((stay) => {
      const pending =
        getPendingAmount(stay);

      result.totalPending +=
        pending;

      const status =
        lower(stay.status);

      if (
        status === "checked_out" ||
        stay.checkedOut === true
      ) {
        result.checkedOutPending +=
          1;
      } else {
        result.checkedInPending +=
          1;
      }

      if (
        getPaymentStatus(stay) ===
        "partial"
      ) {
        result.partialPayments +=
          1;
      } else {
        result.noPayment += 1;
      }

      const pendingDate =
        getDateObject(
          stay.actualCheckOutAt ||
            stay.checkedOutAt ||
            stay.checkInAt
        );

      if (
        pendingDate &&
        (!result.oldestPendingAt ||
          pendingDate <
            result.oldestPendingAt)
      ) {
        result.oldestPendingAt =
          pendingDate;
      }
    });

    result.totalPending =
      roundMoney(
        result.totalPending
      );

    return result;
  }, [stays]);

  /* ------------------------------------------------------------------------ */
  /*                          Filter and Sorting                               */
  /* ------------------------------------------------------------------------ */

  const filteredStays = useMemo(() => {
    const searchTerm =
      lower(search);

    const filtered =
      stays.filter((stay) => {
        const primaryGuest =
          getPrimaryGuest(stay);

        const searchText = [
          stay.room,

          primaryGuest.name,

          primaryGuest.phoneNumber,

          stay.invoiceNumber,

          stay.adRegisterSerialNo,

          stay.bookingSource,

          getBookingReference(
            stay
          ),

          stay.pendingReason,

          stay.managerNote,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const status =
          lower(stay.status);

        const matchesSearch =
          !searchTerm ||
          searchText.includes(
            searchTerm
          );

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter ===
            "pending" &&
            getPendingAmount(
              stay
            ) > 0) ||
          (statusFilter ===
            "checked_in" &&
            status !==
              "checked_out" &&
            stay.checkedOut !==
              true) ||
          (statusFilter ===
            "checked_out" &&
            (status ===
              "checked_out" ||
              stay.checkedOut ===
                true)) ||
          (statusFilter ===
            "partial" &&
            getPaymentStatus(
              stay
            ) === "partial");

        return (
          matchesSearch &&
          matchesStatus
        );
      });

    return [...filtered].sort(
      (firstStay, secondStay) => {
        if (sortBy === "lowest") {
          return (
            getPendingAmount(
              firstStay
            ) -
            getPendingAmount(
              secondStay
            )
          );
        }

        if (sortBy === "recent") {
          return (
            getTimestamp(
              secondStay.actualCheckOutAt ||
                secondStay.checkedOutAt ||
                secondStay.checkInAt
            ) -
            getTimestamp(
              firstStay.actualCheckOutAt ||
                firstStay.checkedOutAt ||
                firstStay.checkInAt
            )
          );
        }

        if (sortBy === "oldest") {
          return (
            getTimestamp(
              firstStay.actualCheckOutAt ||
                firstStay.checkedOutAt ||
                firstStay.checkInAt
            ) -
            getTimestamp(
              secondStay.actualCheckOutAt ||
                secondStay.checkedOutAt ||
                secondStay.checkInAt
            )
          );
        }

        if (sortBy === "room") {
          return normalizeRoom(
            firstStay.room
          ).localeCompare(
            normalizeRoom(
              secondStay.room
            ),
            undefined,
            {
              numeric: true,
            }
          );
        }

        if (sortBy === "guest") {
          return clean(
            getPrimaryGuest(
              firstStay
            ).name
          ).localeCompare(
            clean(
              getPrimaryGuest(
                secondStay
              ).name
            )
          );
        }

        return (
          getPendingAmount(
            secondStay
          ) -
          getPendingAmount(
            firstStay
          )
        );
      }
    );
  }, [
    stays,
    search,
    statusFilter,
    sortBy,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                         Open Payment Modal                                */
  /* ------------------------------------------------------------------------ */

  const openPayment = (stay) => {
    const pending =
      getPendingAmount(stay);

    setSelectedStay(stay);

    setPaymentForm(
      createPaymentForm(
        pending
      )
    );

    setErrors({});
  };

  /* ------------------------------------------------------------------------ */
  /*                            Form Updater                                   */
  /* ------------------------------------------------------------------------ */

  const updatePaymentForm = (
    field,
    value
  ) => {
    setPaymentForm(
      (current) => {
        const next = {
          ...current,
          [field]: value,
        };

        if (
          field ===
            "paymentMode" &&
          value === "Cash"
        ) {
          next.transactionReference =
            "";
        }

        return next;
      }
    );

    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = {
        ...current,
      };

      delete next[field];

      return next;
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                              Validation                                   */
  /* ------------------------------------------------------------------------ */

  const validatePayment = () => {
    const nextErrors = {};

    if (!selectedStay) {
      nextErrors.stay =
        "Stay record missing hai.";
    }

    const amount = toAmount(
      paymentForm.amount
    );

    if (amount <= 0) {
      nextErrors.amount =
        "Valid payment amount required hai.";
    }

    if (
      amount >
      getPendingAmount(
        selectedStay
      )
    ) {
      nextErrors.amount =
        "Payment pending balance se zyada nahi ho sakti.";
    }

    if (
      !clean(
        paymentForm.paymentMode
      )
    ) {
      nextErrors.paymentMode =
        "Payment mode required hai.";
    }

    if (
      paymentForm.paymentMode !==
        "Cash" &&
      !clean(
        paymentForm.transactionReference
      )
    ) {
      nextErrors.transactionReference =
        "Online payment ka transaction reference required hai.";
    }

    if (
      !clean(
        paymentForm.receivedBy
      )
    ) {
      nextErrors.receivedBy =
        "Payment receive karne wale Manager ka naam required hai.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                          Receive Pending Payment                          */
  /* ------------------------------------------------------------------------ */

  const receivePayment = async (
    event
  ) => {
    event.preventDefault();

    if (
      saving ||
      !validatePayment()
    ) {
      return;
    }

    const stayId =
      selectedStay.stayId ||
      selectedStay.id;

    if (!stayId) {
      alert(
        "Stay ID missing hai."
      );

      return;
    }

    const amount = roundMoney(
      Math.max(
        0,
        toAmount(
          paymentForm.amount
        )
      )
    );

    const now = new Date();

    const receiptNumber =
      createReceiptNumber(
        hotelId
      );

    const paymentEntry = {
      amount,

      quantity: 1,

      rate: amount,

      title:
        "Pending Amount Received",

      paymentMode:
        clean(
          paymentForm.paymentMode
        ),

      transactionReference:
        clean(
          paymentForm.transactionReference
        ),

      note:
        clean(
          paymentForm.note
        ) ||
        "Pending balance received",

      receivedBy:
        clean(
          paymentForm.receivedBy
        ),

      source:
        "pending_payment",

      receiptNumber,

      addedAt:
        now.toISOString(),
    };

    setSaving(true);

    try {
      const stayReference = doc(
        db,
        "hotelStays",
        stayId
      );

      const dayBookReference =
        doc(
          db,
          "dayBookEntries",
          `PENDING_${String(
            receiptNumber
          ).replace(
            /[^a-zA-Z0-9_-]/g,
            "_"
          )}`
        );

      const invoiceReference =
        selectedStay.invoiceNumber
          ? doc(
              db,
              "hotelInvoices",
              selectedStay.invoiceNumber
            )
          : null;

      /*
       * Phase 2:
       * Stay balance + payment history + Day Book pending receipt + invoice
       * ek hi Firestore transaction me update hote hain.
       *
       * Transaction current Firestore balance dubara read karta hai, isliye
       * stale modal / second device se old balance overwrite nahi hota.
       */
      const paymentResult =
        await runTransaction(
          db,
          async (transaction) => {
            const staySnapshot =
              await transaction.get(
                stayReference
              );

            if (
              !staySnapshot.exists()
            ) {
              throw new Error(
                "Stay record Firestore me nahi mila."
              );
            }

            const invoiceSnapshot =
              invoiceReference
                ? await transaction.get(
                    invoiceReference
                  )
                : null;

            const currentStay =
              staySnapshot.data();

            const currentPending = canonicalBill(currentStay).balance;

            if (
              amount >
              currentPending + 0.009
            ) {
              throw new Error(
                `Current pending balance ${formatMoney(
                  currentPending
                )} hai. Payment usse zyada nahi ho sakti.`
              );
            }

            const existingPayments =
              Array.isArray(
                currentStay.payments
              )
                ? currentStay.payments
                : [];

            const alreadySaved =
              existingPayments.some(
                (item) =>
                  clean(
                    item?.receiptNumber
                  ) ===
                  clean(
                    receiptNumber
                  )
              );

            if (alreadySaved) {
              return {
                duplicate: true,
                currentStay,
                updatedPayments:
                  existingPayments,
                newPaid:
                  calculatePaidTotal(currentStay),
                newBalance:
                  currentPending,
                previousBalance:
                  currentPending,
              };
            }

            const currentPaid =
              calculatePaidTotal(currentStay);

            const nextPaid =
              roundMoney(
                currentPaid + amount
              );

            const nextBalance =
              roundMoney(
                Math.max(
                  0,
                  currentPending - amount
                )
              );

            const nextPayments = [
              ...existingPayments,
              paymentEntry,
            ];

            transaction.update(
              stayReference,
              {
                ...financialFields({...currentStay, paidTotal: nextPaid}),
                payments:
                  nextPayments,

                paid:
                  nextPaid,

                paidTotal:
                  nextPaid,

                balance:
                  nextBalance,

                paymentStatus:
                  nextBalance <= 0
                    ? "paid"
                    : "partial",

                pendingCleared:
                  nextBalance <= 0,

                pendingClearedAt:
                  nextBalance <= 0
                    ? now.toISOString()
                    : "",

                lastPaymentAt:
                  now.toISOString(),

                lastPaymentAmount:
                  amount,

                lastPaymentMode:
                  paymentEntry.paymentMode,

                lastPaymentReceiptNumber:
                  receiptNumber,

                updatedAt:
                  serverTimestamp(),
              }
            );

            transaction.set(
              dayBookReference,
              {
                hotelId,

                hotelName:
                  clean(hotelName),

                businessDate:
                  getBusinessDateKey(
                    now
                  ),

                entryType:
                  "payment",

                category:
                  "pending_received",

                title:
                  `Pending Amount Received${
                    currentStay.room
                      ? ` - Room ${normalizeRoom(
                          currentStay.room
                        )}`
                      : ""
                  }`,

                amount,

                paymentMode:
                  paymentEntry.paymentMode,

                transactionReference:
                  paymentEntry.transactionReference,

                room:
                  normalizeRoom(
                    currentStay.room
                  ),

                guestName:
                  getPrimaryGuest(
                    currentStay
                  ).name,

                phoneNumber:
                  getPrimaryGuest(
                    currentStay
                  ).phoneNumber,

                stayId,

                invoiceNumber:
                  currentStay.invoiceNumber ||
                  selectedStay.invoiceNumber ||
                  "",

                receiptNumber,

                operationId:
                  `PENDING_${receiptNumber}`,

                note:
                  `Pending Amount Received – Room No. ${normalizeRoom(
                    currentStay.room
                  )}${
                    paymentEntry.transactionReference
                      ? ` (${paymentEntry.transactionReference})`
                      : ""
                  }`,

                receivedBy:
                  paymentEntry.receivedBy,

                eventAt:
                  now.toISOString(),

                source:
                  "pending_received",

                hideFromRegister:
                  true,

                ledgerRoom:
                  normalizeRoom(
                    currentStay.room
                  ),

                ledgerGuestName:
                  getPrimaryGuest(
                    currentStay
                  ).name,

                ledgerAdvance:
                  amount,

                ledgerNote:
                  `Pending Amount Received – Room No. ${normalizeRoom(
                    currentStay.room
                  )}${
                    paymentEntry.transactionReference
                      ? ` (${paymentEntry.transactionReference})`
                      : ""
                  }`,

                ledgerTransactionReference:
                  paymentEntry.transactionReference ||
                  "",

                createdAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),
              },
              { merge: true }
            );

            if (
              invoiceReference &&
              invoiceSnapshot?.exists()
            ) {
                transaction.update(
                  invoiceReference,
                  {
                    payments:
                      nextPayments,

                    paidTotal:
                      nextPaid,

                    balance:
                      nextBalance,

                    paymentStatus:
                      nextBalance <= 0
                        ? "paid"
                        : "partial",

                    updatedAt:
                      serverTimestamp(),
                  }
                );
            }

            return {
              duplicate: false,
              currentStay,
              updatedPayments:
                nextPayments,
              newPaid:
                nextPaid,
              newBalance:
                nextBalance,
              previousBalance:
                currentPending,
            };
          }
        );

      if (paymentResult.duplicate) {
        alert(
          "Ye payment receipt pehle hi save ho chuki hai. Duplicate entry create nahi ki gayi."
        );
        return;
      }

      const updatedPayments =
        paymentResult.updatedPayments;

      const newPaid =
        paymentResult.newPaid;

      const newBalance =
        paymentResult.newBalance;

      const oldPending =
        paymentResult.previousBalance;

      try {
        await logPaymentReceived({
          hotelId, hotelName: clean(hotelName), room: normalizeRoom(selectedStay.room), stayId,
          guestName: getPrimaryGuest(selectedStay).name, phoneNumber: getPrimaryGuest(selectedStay).phoneNumber,
          invoiceNumber: selectedStay.invoiceNumber || "", receiptNumber,
          referenceId: paymentEntry.transactionReference || receiptNumber, amount, title: "Pending Amount Received",
          description: `Pending payment received • ${paymentEntry.paymentMode || "-"}`,
          metadata: { previousBalance: oldPending, newBalance, paymentMode: paymentEntry.paymentMode, transactionReference: paymentEntry.transactionReference || "" },
          eventAt: now,
        });
      } catch (auditError) { console.warn("Pending payment audit log error:", auditError); }

      const updatedStay = {
        ...selectedStay,

        payments:
          updatedPayments,

        paid:
          newPaid,

        paidTotal:
          newPaid,

        balance:
          newBalance,

        paymentStatus:
          newBalance <= 0
            ? "paid"
            : "partial",

        lastPaymentAt:
          now.toISOString(),
      };

      setSelectedStay(
        updatedStay
      );

      if (
        paymentForm.printReceipt
      ) {
        setReceiptData({
          stay:
            updatedStay,

          payment:
            paymentEntry,
        });
      }

      alert(
        newBalance <= 0
          ? `Payment receive ho gayi ✅\nPending balance fully clear ho gaya.`
          : `Payment receive ho gayi ✅\nRemaining balance: ${formatMoney(
              newBalance
            )}`
      );

      if (newBalance <= 0) {
        setSelectedStay(null);
      } else {
        setPaymentForm(
          createPaymentForm(
            newBalance
          )
        );
      }
    } catch (error) {
      console.error(
        "Pending payment receive error:",
        error
      );

      alert(
        "Payment save nahi hui: " +
          (error?.message ||
            error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <header style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            💰 Pending Payments
          </h3>

          <div style={subtitleStyle}>
            Pending balances, partial
            payments aur recovery tracking
          </div>
        </div>

        <span style={pendingBadgeStyle}>
          {formatMoney(
            summary.totalPending
          )} Pending
        </span>
      </header>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Pending Guests"
          value={
            summary.totalGuests
          }
          danger={
            summary.totalGuests > 0
          }
        />

        <SummaryCard
          label="Total Pending"
          value={formatMoney(
            summary.totalPending
          )}
          danger={
            summary.totalPending > 0
          }
        />

        <SummaryCard
          label="Checked-In Pending"
          value={
            summary.checkedInPending
          }
        />

        <SummaryCard
          label="Checked-Out Pending"
          value={
            summary.checkedOutPending
          }
          warning={
            summary.checkedOutPending >
            0
          }
        />

        <SummaryCard
          label="Partial Payments"
          value={
            summary.partialPayments
          }
        />

        <SummaryCard
          label="No Payment"
          value={
            summary.noPayment
          }
          danger={
            summary.noPayment > 0
          }
        />

        <SummaryCard
          label="Oldest Pending"
          value={
            summary.oldestPendingAt
              ? formatDateTime(
                  summary.oldestPendingAt
                )
              : "-"
          }
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Guest, phone, room, invoice, A&D serial..."
          style={inputStyle}
        />

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="pending">
            All Pending
          </option>

          <option value="all">
            All Records
          </option>

          <option value="checked_in">
            Checked-In Guests
          </option>

          <option value="checked_out">
            Checked-Out Guests
          </option>

          <option value="partial">
            Partial Payment
          </option>
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="highest">
            Highest Pending
          </option>

          <option value="lowest">
            Lowest Pending
          </option>

          <option value="recent">
            Recent First
          </option>

          <option value="oldest">
            Oldest First
          </option>

          <option value="room">
            Room Number
          </option>

          <option value="guest">
            Guest Name
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={emptyBoxStyle}>
          Pending payments loading...
        </div>
      ) : filteredStays.length === 0 ? (
        <div style={emptyBoxStyle}>
          Koi pending payment record nahi
          mila.
        </div>
      ) : (
        <div style={cardGridStyle}>
          {filteredStays.map((stay) => {
            const primaryGuest =
              getPrimaryGuest(stay);

            const bill =
              calculateStayBill(
                stay
              );

            const pending =
              getPendingAmount(stay);

            return (
              <button
                key={stay.id}
                type="button"
                onClick={() =>
                  openPayment(stay)
                }
                style={pendingCardStyle}
              >
                <div style={cardTopStyle}>
                  <div>
                    <div style={roomStyle}>
                      Room{" "}
                      {normalizeRoom(
                        stay.room
                      ) || "-"}
                    </div>

                    <div style={guestStyle}>
                      {primaryGuest.name ||
                        "Guest"}
                    </div>

                    <div style={phoneStyle}>
                      📞{" "}
                      {primaryGuest.phoneNumber ||
                        "-"}
                    </div>
                  </div>

                  <PaymentStatusBadge
                    status={getPaymentStatus(
                      stay
                    )}
                  />
                </div>

                <div style={amountGridStyle}>
                  <AmountBox
                    label="Bill Total"
                    value={
                      bill.grandTotal
                    }
                  />

                  <AmountBox
                    label="Paid"
                    value={bill.paid}
                  />

                  <AmountBox
                    label="Pending"
                    value={pending}
                    danger
                  />
                </div>

                <div style={metaGridStyle}>
                  <MetaItem
                    label="Invoice"
                    value={
                      stay.invoiceNumber ||
                      "-"
                    }
                  />

                  <MetaItem
                    label="Booking Source"
                    value={
                      stay.bookingSource ||
                      "Walk-In"
                    }
                  />

                  <MetaItem
                    label="Checkout"
                    value={formatDateTime(
                      stay.actualCheckOutAt ||
                        stay.checkedOutAt
                    )}
                  />

                  <MetaItem
                    label="Pending Reason"
                    value={
                      stay.pendingReason ||
                      "-"
                    }
                  />
                </div>

                <div style={receiveTextStyle}>
                  Receive Payment →
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedStay && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              setSelectedStay(null);
            }
          }}
        >
          <form
            onSubmit={receivePayment}
            style={modalStyle}
          >
            <header style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  Receive Pending Payment
                </h2>

                <div style={subtitleStyle}>
                  Room{" "}
                  {normalizeRoom(
                    selectedStay.room
                  ) || "-"}{" "}
                  •{" "}
                  {
                    getPrimaryGuest(
                      selectedStay
                    ).name
                  }
                </div>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setSelectedStay(
                    null
                  )
                }
                style={closeButtonStyle}
              >
                ×
              </button>
            </header>

            <div style={modalSummaryGridStyle}>
              <SummaryCard
                label="Grand Total"
                value={formatMoney(
                  calculateStayBill(
                    selectedStay
                  ).grandTotal
                )}
              />

              <SummaryCard
                label="Already Paid"
                value={formatMoney(
                  calculateStayBill(
                    selectedStay
                  ).paid
                )}
              />

              <SummaryCard
                label="Pending Balance"
                value={formatMoney(
                  getPendingAmount(
                    selectedStay
                  )
                )}
                danger
              />

              <SummaryCard
                label="Invoice Number"
                value={
                  selectedStay.invoiceNumber ||
                  "-"
                }
              />
            </div>

            <div style={formGridStyle}>
              <Field
                label="Amount Received"
                required
                error={errors.amount}
              >
                <input
                  type="number"
                  min="0"
                  max={getPendingAmount(
                    selectedStay
                  )}
                  value={
                    paymentForm.amount
                  }
                  disabled={saving}
                  onChange={(event) =>
                    updatePaymentForm(
                      "amount",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorStyle(
                      errors.amount
                    ),
                  }}
                  placeholder="₹"
                />
              </Field>

              <Field
                label="Payment Mode"
                required
                error={
                  errors.paymentMode
                }
              >
                <select
                  value={
                    paymentForm.paymentMode
                  }
                  disabled={saving}
                  onChange={(event) =>
                    updatePaymentForm(
                      "paymentMode",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorStyle(
                      errors.paymentMode
                    ),
                  }}
                >
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Card</option>
                  <option>
                    Bank Transfer
                  </option>
                  <option>Fab</option>
                  <option>Brevistay</option>
                  <option>Other OTA</option>
                </select>
              </Field>

              {paymentForm.paymentMode !==
                "Cash" && (
                <Field
                  label="Transaction Reference"
                  required
                  error={
                    errors.transactionReference
                  }
                >
                  <input
                    value={
                      paymentForm.transactionReference
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updatePaymentForm(
                        "transactionReference",
                        event.target.value
                      )
                    }
                    style={{
                      ...inputStyle,
                      ...getErrorStyle(
                        errors.transactionReference
                      ),
                    }}
                    placeholder="UPI / Card / Bank code"
                  />
                </Field>
              )}

              <Field
                label="Received By"
                required
                error={
                  errors.receivedBy
                }
              >
                <input
                  value={
                    paymentForm.receivedBy
                  }
                  disabled={saving}
                  onChange={(event) =>
                    updatePaymentForm(
                      "receivedBy",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorStyle(
                      errors.receivedBy
                    ),
                  }}
                  placeholder="Manager name"
                />
              </Field>

              <Field label="Payment Note">
                <input
                  value={
                    paymentForm.note
                  }
                  disabled={saving}
                  onChange={(event) =>
                    updatePaymentForm(
                      "note",
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Pending amount received"
                />
              </Field>
            </div>

            <label style={printOptionStyle}>
              <input
                type="checkbox"
                checked={
                  paymentForm.printReceipt
                }
                disabled={saving}
                onChange={(event) =>
                  updatePaymentForm(
                    "printReceipt",
                    event.target.checked
                  )
                }
              />

              Payment receipt open karein
            </label>

            <footer style={modalFooterStyle}>
              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setSelectedStay(null)
                }
                style={cancelButtonStyle}
              >
                Cancel
              </button>

              <button
                disabled={saving}
                style={{
                  ...receiveButtonStyle,

                  opacity: saving
                    ? 0.6
                    : 1,
                }}
              >
                {saving
                  ? "Payment Saving..."
                  : `Receive ${formatMoney(
                      paymentForm.amount
                    )}`}
              </button>
            </footer>
          </form>
        </div>
      )}

      {receiptData && (
        <PaymentReceiptModal
          stay={
            receiptData.stay
          }
          payment={
            receiptData.payment
          }
          hotelName={
            hotelName || hotelId
          }
          receiptNumber={
            receiptData.payment
              .receiptNumber
          }
          onClose={() =>
            setReceiptData(null)
          }
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  danger = false,
  warning = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : warning
            ? "#c2410c"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function AmountBox({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={amountBoxStyle}>
      <span style={amountLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...amountValueStyle,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </strong>
    </div>
  );
}

function MetaItem({
  label,
  value,
}) {
  return (
    <div>
      <span style={metaLabelStyle}>
        {label}
      </span>

      <strong style={metaValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function PaymentStatusBadge({
  status,
}) {
  const config = {
    pending: {
      label: "Payment Pending",
      background: "#fee2e2",
      color: "#b91c1c",
    },

    partial: {
      label: "Partially Paid",
      background: "#fef3c7",
      color: "#92400e",
    },

    paid: {
      label: "Paid",
      background: "#dcfce7",
      color: "#15803d",
    },
  };

  const selected =
    config[status] ||
    config.pending;

  return (
    <span
      style={{
        ...statusBadgeStyle,

        background:
          selected.background,

        color:
          selected.color,
      }}
    >
      {selected.label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const getErrorStyle = (error) =>
  error
    ? {
        borderColor: "#dc2626",

        boxShadow:
          "0 0 0 2px rgba(220,38,38,.08)",
      }
    : {};

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "center",

  gap: 12,

  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,

  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 11,
};

const pendingBadgeStyle = {
  padding: "8px 12px",

  borderRadius: 999,

  background: "#fee2e2",

  color: "#b91c1c",

  fontWeight: 900,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(130px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,

  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 16,

  fontWeight: 900,

  overflowWrap: "anywhere",
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(230px,1fr) repeat(2,minmax(150px,210px))",

  gap: 9,

  marginTop: 15,
};

const inputStyle = {
  width: "100%",

  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",

  color: "#0f172a",

  boxSizing: "border-box",

  outline: "none",
};

const errorBoxStyle = {
  marginTop: 12,

  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",

  color: "#b91c1c",

  fontSize: 11,

  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 13,

  padding: 18,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 11,

  color: "#64748b",

  textAlign: "center",
};

const cardGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(290px,1fr))",

  gap: 11,

  marginTop: 15,
};

const pendingCardStyle = {
  padding: 14,

  border:
    "1px solid #fecaca",

  borderRadius: 14,

  background: "#fffafa",

  textAlign: "left",

  cursor: "pointer",

  boxShadow:
    "0 4px 14px rgba(15,23,42,.04)",
};

const cardTopStyle = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "flex-start",

  gap: 10,
};

const roomStyle = {
  color: "#0f172a",

  fontSize: 17,

  fontWeight: 900,
};

const guestStyle = {
  marginTop: 5,

  color: "#334155",

  fontSize: 13,

  fontWeight: 800,
};

const phoneStyle = {
  marginTop: 3,

  color: "#64748b",

  fontSize: 10,
};

const statusBadgeStyle = {
  padding: "5px 8px",

  borderRadius: 999,

  fontSize: 9,

  fontWeight: 900,

  whiteSpace: "nowrap",
};

const amountGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(3,minmax(0,1fr))",

  gap: 8,

  marginTop: 13,
};

const amountBoxStyle = {
  padding: 8,

  borderRadius: 9,

  background: "#ffffff",

  border:
    "1px solid #e2e8f0",
};

const amountLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,

  fontWeight: 800,

  textTransform: "uppercase",
};

const amountValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 11,

  fontWeight: 900,
};

const metaGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(2,minmax(0,1fr))",

  gap: 9,

  marginTop: 12,

  paddingTop: 10,

  borderTop:
    "1px solid #e2e8f0",
};

const metaLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,

  fontWeight: 800,

  textTransform: "uppercase",
};

const metaValueStyle = {
  display: "block",

  marginTop: 3,

  color: "#334155",

  fontSize: 10,

  fontWeight: 800,

  overflowWrap: "anywhere",
};

const receiveTextStyle = {
  marginTop: 11,

  color: "#2563eb",

  fontSize: 10,

  fontWeight: 900,

  textAlign: "right",
};

const overlayStyle = {
  position: "fixed",

  inset: 0,

  zIndex: 30000,

  display: "flex",

  alignItems: "center",

  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(760px,100%)",

  maxHeight: "95vh",

  overflowY: "auto",

  padding: 19,

  borderRadius: 20,

  background: "#ffffff",

  boxSizing: "border-box",
};

const modalHeaderStyle = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "flex-start",

  gap: 12,

  paddingBottom: 13,

  borderBottom:
    "1px solid #e2e8f0",
};

const modalTitleStyle = {
  margin: 0,

  color: "#0f172a",
};

const closeButtonStyle = {
  width: 38,

  height: 38,

  border: 0,

  borderRadius: 10,

  background: "#f1f5f9",

  fontSize: 25,

  cursor: "pointer",
};

const modalSummaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(135px,1fr))",

  gap: 9,

  marginTop: 14,
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(210px,1fr))",

  gap: 10,

  marginTop: 14,
};

const fieldStyle = {
  display: "block",
};

const fieldLabelStyle = {
  display: "block",

  marginBottom: 6,

  color: "#334155",

  fontSize: 11,

  fontWeight: 800,
};

const requiredStyle = {
  color: "#dc2626",
};

const fieldErrorStyle = {
  display: "block",

  marginTop: 5,

  color: "#dc2626",

  fontSize: 9,

  fontWeight: 700,
};

const printOptionStyle = {
  display: "flex",

  alignItems: "center",

  gap: 8,

  marginTop: 13,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  background: "#f8fafc",

  color: "#334155",

  fontSize: 11,

  fontWeight: 800,
};

const modalFooterStyle = {
  display: "flex",

  justifyContent: "flex-end",

  gap: 9,

  marginTop: 15,

  paddingTop: 13,

  borderTop:
    "1px solid #e2e8f0",
};

const cancelButtonStyle = {
  padding: "10px 14px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",

  color: "#334155",

  fontWeight: 800,

  cursor: "pointer",
};

const receiveButtonStyle = {
  padding: "10px 15px",

  border: 0,

  borderRadius: 9,

  background: "#16a34a",

  color: "#ffffff",

  fontWeight: 900,

  cursor: "pointer",
};

export default PendingPaymentsPanel;